import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  fetchAdminTenantAddresses,
  createAdminTenantAddress,
  patchAdminTenantAddress,
  deleteAdminTenantAddress,
  recordAdminTenantAddressChange,
} from "../../../api/adminData";
import { inputClass, labelClass } from "./tenantDetailStyles";
import { todayBusinessDate } from "../../../utils/datetime";

/**
 * Tenant address book section (Cluster A / A2).
 *
 * Self-contained: fetches a tenant's addresses, lets the admin add / edit /
 * soft-delete them and set exactly one current address. The flat
 * Tenant.street/postal_code/city/country fields stay the legacy surface and are
 * mirrored server-side from the current address; when no address rows exist yet
 * this shows the legacy address with an "adopt as record" affordance.
 *
 * onCurrentChanged() is called after any op that can change the legacy flat
 * fields (set-current / adopt) so the parent can refresh the tenant.
 */
const ADDRESS_TYPE_LABELS = {
  private: "Privat",
  billing: "Rechnung",
  correspondence: "Korrespondenz",
  home_abroad: "Ausland",
  previous: "Frühere",
  other: "Andere",
};

const ADDRESS_TYPE_OPTIONS = [
  "private",
  "billing",
  "correspondence",
  "home_abroad",
  "previous",
  "other",
];

// Option C: the residential ("private") address is created/changed ONLY via the
// residential paths (first-residential / adopt-legacy / Phase C "Adresswechsel
// erfassen"). The generic Add/Edit form under "Weitere Adressen" may therefore
// only pick non-residential types.
const NON_RESIDENTIAL_ADDRESS_TYPES = ADDRESS_TYPE_OPTIONS.filter(
  (t) => t !== "private"
);
const ADD_DEFAULT_ADDRESS_TYPE = "correspondence";

// Type options for the Add/Edit form: when editing the current residential
// address the type is locked to "private"; otherwise only non-residential types.
function getAddressTypeOptions({ editingResidential }) {
  return editingResidential ? ["private"] : NON_RESIDENTIAL_ADDRESS_TYPES;
}

function emptyAddressForm() {
  return {
    address_type: ADD_DEFAULT_ADDRESS_TYPE,
    label: "",
    street: "",
    postal_code: "",
    city: "",
    canton: "",
    country: "CH",
    valid_from: "",
    valid_to: "",
    notes: "",
    is_current: false,
  };
}

function addressToForm(a) {
  return {
    address_type: a.address_type || "private",
    label: a.label || "",
    street: a.street || "",
    postal_code: a.postal_code || "",
    city: a.city || "",
    canton: a.canton || "",
    country: a.country || "",
    valid_from: a.valid_from || "",
    valid_to: a.valid_to || "",
    notes: a.notes || "",
    is_current: false,
  };
}

function buildBody(form) {
  const trim = (v) => {
    const s = String(v || "").trim();
    return s || null;
  };
  return {
    address_type: form.address_type || "private",
    label: trim(form.label),
    street: trim(form.street),
    postal_code: trim(form.postal_code),
    city: trim(form.city),
    canton: trim(form.canton),
    country: trim(form.country),
    valid_from: trim(form.valid_from),
    valid_to: trim(form.valid_to),
    notes: trim(form.notes),
  };
}

const RESIDENTIAL_TYPE = "private";

// "YYYY-MM-DD" -> "DD.MM.YYYY" (null/empty -> null).
function formatDateDe(iso) {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (!m) return String(iso);
  return `${m[3]}.${m[2]}.${m[1]}`;
}

// Period label for a former residential address.
function formatAddressPeriod(a) {
  const from = formatDateDe(a.valid_from);
  const to = formatDateDe(a.valid_to);
  if (from && to) return `${from} – ${to}`;
  if (to) return `bis ${to}`;
  if (from) return `seit ${from}`;
  return "Zeitraum unbekannt";
}

// Option C split: the single current residential, the former residential
// periods, and every other (non-residential) stored address.
function classifyAddresses(addresses) {
  const list = Array.isArray(addresses) ? addresses : [];
  const isResidential = (a) => a.address_type === RESIDENTIAL_TYPE;
  const current =
    list.find((a) => isResidential(a) && a.is_current === true) || null;
  const former = list.filter(
    (a) => isResidential(a) && a.is_current !== true
  );
  const other = list.filter((a) => !isResidential(a));
  return { current, former, other };
}

function AddressLines({ a }) {
  return (
    <>
      <div className="mt-1 text-[13px] text-[#0f172a] dark:text-[#eef2ff]">
        {a.street || "—"}
      </div>
      <div className="text-[12px] text-[#64748b] dark:text-[#8090b0]">
        {[a.postal_code, a.city].filter(Boolean).join(" ")}
        {a.country ? ` · ${a.country}` : ""}
      </div>
    </>
  );
}

const sectionTitleClass =
  "text-[10px] font-bold uppercase tracking-[0.6px] text-[#64748b] dark:text-[#6b7a9a]";
const emptyHintClass =
  "m-0 text-[12px] text-[#64748b] dark:text-[#6b7a9a]";
const cardClass =
  "rounded-[8px] border border-black/10 p-3 dark:border-white/[0.07]";

// "Heute" as YYYY-MM-DD on the SWISS calendar.
//
// This used to shift the instant by the DEVICE's offset, which made the
// default, the "not in the future" check and the input's `max` all agree
// with the admin's own wall calendar — and disagree with the server's.
// `tenant_address_service` validates `valid_from > zurich_today()`, so for an
// admin working from a zone ahead of Zurich the form happily accepted a date
// the backend then rejected. One clock now, and it is the backend's.
function todayIsoDate() {
  return todayBusinessDate();
}

function emptyChangeForm() {
  return {
    valid_from: todayIsoDate(),
    street: "",
    postal_code: "",
    city: "",
    canton: "",
    country: "CH",
    label: "",
    notes: "",
  };
}

/**
 * Dedicated "Adresswechsel erfassen" (residential move) panel — intentionally
 * separate from the Add/Edit form so it never mixes with address_type /
 * is_current / the Phase-B type restrictions. A move is a NEW address: fields
 * start empty (valid_from=today, country=CH), never prefilled from the current
 * address. Submit-side validation mirrors the backend rules.
 */
function AddressChangeModal({ open, currentValidFrom, saving, onClose, onSubmit }) {
  const [form, setForm] = useState(emptyChangeForm);
  const [error, setError] = useState(null);
  const panelRef = useRef(null);

  // Reset to empty (today + CH) on every open — a move is not an edit.
  useEffect(() => {
    if (open) {
      setForm(emptyChangeForm());
      setError(null);
    }
  }, [open]);

  // The panel renders at the very end of the address book, well below the
  // link that opens it, so bring it into view once per opening. Keyed on
  // `open` alone: typing, saving or a reload of the book never scrolls again;
  // only closing and reopening does. Same one-frame scroll as the page's
  // tenancy editor (AdminTenantDetailPage). Focus stays where it was.
  useEffect(() => {
    if (!open) return undefined;
    const id = requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  if (!open) return null;

  const set = (key) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const submit = () => {
    setError(null);
    const trim = (v) => String(v || "").trim();
    const vf = trim(form.valid_from);
    if (!vf) {
      setError("Bitte ein Gültig-ab-Datum erfassen.");
      return;
    }
    if (vf > todayIsoDate()) {
      setError("Das Gültig-ab-Datum darf nicht in der Zukunft liegen.");
      return;
    }
    if (currentValidFrom && vf <= currentValidFrom) {
      setError(
        "Das neue Gültig-ab-Datum muss nach dem Beginn der aktuellen Wohnadresse liegen."
      );
      return;
    }
    const street = trim(form.street);
    const postal_code = trim(form.postal_code);
    const city = trim(form.city);
    const country = trim(form.country);
    if (!street || !postal_code || !city || !country) {
      setError(
        "Bitte Strasse, PLZ, Ort und Land der neuen Wohnadresse erfassen."
      );
      return;
    }
    const orNull = (v) => trim(v) || null;
    // Body intentionally omits address_type / is_current / valid_to — the server
    // sets those.
    onSubmit({
      valid_from: vf,
      street,
      postal_code,
      city,
      canton: orNull(form.canton),
      country,
      label: orNull(form.label),
      notes: orNull(form.notes),
    });
  };

  const field = (id, key, label, opts = {}) => (
    <div style={opts.full ? { gridColumn: "1 / -1" } : undefined}>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <input
        id={id}
        type={opts.type || "text"}
        max={opts.max}
        className={inputClass}
        value={form[key]}
        onChange={set(key)}
        disabled={saving}
      />
    </div>
  );

  return (
    <div
      ref={panelRef}
      data-testid="address-change-modal"
      className="mt-3 rounded-[10px] border border-black/10 p-3 dark:border-white/[0.07]"
    >
      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]">
        Adresswechsel erfassen
      </div>
      {error ? <p className="mb-2 text-[12px] text-[#f87171]">{error}</p> : null}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: "10px",
        }}
      >
        {field("change-valid-from", "valid_from", "Gültig ab", {
          type: "date",
          max: todayIsoDate(),
        })}
        {field("change-street", "street", "Strasse", { full: true })}
        {field("change-postal-code", "postal_code", "PLZ")}
        {field("change-city", "city", "Ort")}
        {field("change-canton", "canton", "Kanton")}
        {field("change-country", "country", "Land")}
        {field("change-label", "label", "Bezeichnung", { full: true })}
        {field("change-notes", "notes", "Notizen", { full: true })}
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          className="rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-3.5 py-2 text-[13px] font-semibold text-white"
          style={{ border: "none", cursor: saving ? "default" : "pointer" }}
        >
          {saving ? "Speichern …" : "Adresswechsel speichern"}
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="rounded-[8px] border border-black/10 bg-transparent px-3.5 py-2 text-[13px] font-semibold text-[#64748b] dark:border-white/[0.1] dark:text-[#8090b0]"
          style={{ cursor: saving ? "default" : "pointer" }}
        >
          Abbrechen
        </button>
      </div>
    </div>
  );
}

export default function TenantAddressesSection({
  tenantId,
  tenant,
  onCurrentChanged,
}) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyAddressForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeSaving, setChangeSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchAdminTenantAddresses(tenantId);
      setAddresses(Array.isArray(data) ? data : []);
    } catch (e) {
      setLoadError(e?.message || "Adressen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyAddressForm());
    setFormError(null);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyAddressForm());
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditingId(String(a.id));
    setForm(addressToForm(a));
    setFormError(null);
    setShowForm(true);
  };

  const setField = (key) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const submitForm = async () => {
    setFormError(null);
    if (form.valid_from && form.valid_to && form.valid_to < form.valid_from) {
      setFormError("Gültig bis darf nicht vor Gültig ab liegen.");
      return;
    }
    setSaving(true);
    try {
      const body = buildBody(form);
      // Option C form guard: the generic Add/Edit form must never create a
      // residential ("private") address — that path is reserved for adopt-legacy
      // and (Phase C) "Adresswechsel erfassen". Editing the current residential
      // keeps its "private" type (the select is locked); every other form use
      // is coerced to a non-residential type. (Applies only here, NOT to
      // adoptLegacy.)
      const submittingResidentialEdit =
        editingId != null && form.address_type === RESIDENTIAL_TYPE;
      if (!submittingResidentialEdit && body.address_type === RESIDENTIAL_TYPE) {
        body.address_type = ADD_DEFAULT_ADDRESS_TYPE;
      }
      if (editingId) {
        await patchAdminTenantAddress(editingId, body);
        toast.success("Adresse aktualisiert.");
      } else {
        // Option C: currentness is decided server-side (first residential
        // auto-current; non-residential always stored is_current=false). The
        // UI no longer offers an "is_current" toggle.
        await createAdminTenantAddress(tenantId, body);
        toast.success("Adresse hinzugefügt.");
      }
      closeForm();
      await reload();
      if (onCurrentChanged) onCurrentChanged();
    } catch (e) {
      setFormError(e?.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setBusyId(String(id));
    try {
      await deleteAdminTenantAddress(id);
      toast.success("Adresse gelöscht.");
      await reload();
      if (onCurrentChanged) onCurrentChanged();
    } catch (e) {
      toast.error(e?.message || "Adresse konnte nicht gelöscht werden.");
    } finally {
      setBusyId(null);
    }
  };

  const adoptLegacy = async () => {
    setBusyId("legacy");
    try {
      await createAdminTenantAddress(tenantId, {
        address_type: "private",
        street: tenant?.street || null,
        postal_code: tenant?.postal_code || null,
        city: tenant?.city || null,
        country: tenant?.country || null,
        is_current: true,
      });
      toast.success("Adresse als Datensatz übernommen.");
      await reload();
      if (onCurrentChanged) onCurrentChanged();
    } catch (e) {
      toast.error(e?.message || "Übernahme fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  };

  const openAddressChange = () => setChangeOpen(true);

  // Residential move: the dedicated modal validated the body (no address_type /
  // is_current / valid_to). On success reload + notify the parent (so the
  // Verlauf + flat fields refresh); on error keep the modal open.
  const handleAddressChangeSubmit = async (body) => {
    setChangeSaving(true);
    try {
      await recordAdminTenantAddressChange(tenantId, body);
      toast.success("Adresswechsel erfasst.");
      setChangeOpen(false);
      await reload();
      if (onCurrentChanged) onCurrentChanged();
    } catch (e) {
      toast.error(e?.message || "Adresswechsel fehlgeschlagen.");
    } finally {
      setChangeSaving(false);
    }
  };

  const legacyHasAddress = !!(
    tenant &&
    (String(tenant.street || "").trim() ||
      String(tenant.postal_code || "").trim() ||
      String(tenant.city || "").trim())
  );

  const { current, former, other } = classifyAddresses(addresses);

  // Editing the current residential address -> type is locked to "private".
  const editingResidential =
    editingId != null && form.address_type === RESIDENTIAL_TYPE;
  const typeOptions = getAddressTypeOptions({ editingResidential });

  return (
    <div data-testid="tenant-addresses-section">
      <div className="mb-3">
        <div className={labelClass} style={{ marginBottom: 0 }}>
          Adressen
        </div>
      </div>

      {loadError ? (
        <p className="m-0 text-[12px] text-[#f87171]">{loadError}</p>
      ) : null}

      {loading ? (
        <p className="m-0 text-[12px] text-[#64748b] dark:text-[#6b7a9a]">
          Lade Adressen …
        </p>
      ) : (
        <div className="space-y-4">
          {/* 1. Aktuelle Wohnadresse (the single current private address) */}
          <section data-testid="addr-current-section">
            <div className={sectionTitleClass}>Aktuelle Wohnadresse</div>
            {current ? (
              <div className={`mt-2 ${cardClass}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-[0.6px] text-[#64748b] dark:text-[#6b7a9a]">
                        {ADDRESS_TYPE_LABELS[RESIDENTIAL_TYPE]}
                        {current.label ? ` · ${current.label}` : ""}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-[1px] text-[10px] font-semibold text-emerald-500">
                        Aktuell
                      </span>
                    </div>
                    <AddressLines a={current} />
                    {formatDateDe(current.valid_from) ? (
                      <div className="mt-1 text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
                        {`gültig seit ${formatDateDe(current.valid_from)}`}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <button
                      type="button"
                      onClick={openAddressChange}
                      className="text-[12px] font-semibold text-[#5b8cff] underline"
                      style={{ cursor: "pointer" }}
                    >
                      Adresswechsel erfassen
                    </button>
                    <button
                      type="button"
                      onClick={() => openEdit(current)}
                      className="text-[12px] font-semibold text-[#64748b] underline dark:text-[#8090b0]"
                      style={{ cursor: "pointer" }}
                    >
                      Bearbeiten
                    </button>
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
                  Bei einem Umzug bitte „Adresswechsel erfassen" nutzen.
                  „Bearbeiten" nur für Korrekturen.
                </p>
              </div>
            ) : legacyHasAddress ? (
              <div className={`mt-2 ${cardClass}`}>
                <div className="text-[10px] font-bold uppercase tracking-[0.6px] text-[#64748b] dark:text-[#6b7a9a]">
                  Aktuelle Adresse aus Stammdaten
                </div>
                <AddressLines
                  a={{
                    street: tenant?.street,
                    postal_code: tenant?.postal_code,
                    city: tenant?.city,
                    country: tenant?.country,
                  }}
                />
                <button
                  type="button"
                  onClick={adoptLegacy}
                  disabled={busyId === "legacy"}
                  className="mt-2 rounded-[8px] border border-black/10 bg-transparent px-3 py-1.5 text-[12px] font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04]"
                  style={{ cursor: busyId === "legacy" ? "default" : "pointer" }}
                >
                  Als Adressdatensatz übernehmen
                </button>
              </div>
            ) : (
              <p className={`mt-2 ${emptyHintClass}`}>
                Keine aktuelle Wohnadresse hinterlegt.
              </p>
            )}
          </section>

          {/* 2. Frühere Wohnadressen (private, not current) — read-only in v1 */}
          <section data-testid="addr-former-section">
            <div className={sectionTitleClass}>Frühere Wohnadressen</div>
            {former.length ? (
              <ul className="mt-2 m-0 list-none space-y-2 p-0">
                {former.map((a) => (
                  <li key={String(a.id)} className={cardClass}>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-[0.6px] text-[#64748b] dark:text-[#6b7a9a]">
                        {ADDRESS_TYPE_LABELS[RESIDENTIAL_TYPE]}
                      </span>
                      <span className="inline-flex items-center rounded-full border border-black/10 px-2 py-[1px] text-[10px] font-semibold text-[#64748b] dark:border-white/[0.1] dark:text-[#8090b0]">
                        Früher
                      </span>
                    </div>
                    <AddressLines a={a} />
                    <div className="mt-1 text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
                      {formatAddressPeriod(a)}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={`mt-2 ${emptyHintClass}`}>
                Keine früheren Wohnadressen vorhanden.
              </p>
            )}
          </section>

          {/* 3. Weitere Adressen (non-residential stored addresses) */}
          <section data-testid="addr-other-section">
            <div className="flex items-center justify-between">
              <div className={sectionTitleClass}>Weitere Adressen</div>
              <button
                type="button"
                onClick={openAdd}
                disabled={saving}
                className="rounded-[8px] border border-black/10 bg-transparent px-3 py-1.5 text-[12px] font-semibold text-[#64748b] hover:bg-slate-100 dark:border-white/[0.1] dark:text-[#8090b0] dark:hover:bg-white/[0.04]"
                style={{ cursor: saving ? "default" : "pointer" }}
              >
                Hinzufügen
              </button>
            </div>
            {other.length ? (
              <ul className="mt-2 m-0 list-none space-y-2 p-0">
                {other.map((a) => (
                  <li key={String(a.id)} className={cardClass}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-[0.6px] text-[#64748b] dark:text-[#6b7a9a]">
                            {ADDRESS_TYPE_LABELS[a.address_type] || a.address_type}
                            {a.label ? ` · ${a.label}` : ""}
                          </span>
                        </div>
                        <AddressLines a={a} />
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(a)}
                          className="text-[12px] font-semibold text-[#64748b] underline dark:text-[#8090b0]"
                          style={{ cursor: "pointer" }}
                        >
                          Bearbeiten
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(a.id)}
                          disabled={busyId === String(a.id)}
                          className="text-[12px] font-semibold text-[#f87171] underline"
                          style={{
                            cursor: busyId === String(a.id) ? "default" : "pointer",
                          }}
                        >
                          Löschen
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className={`mt-2 ${emptyHintClass}`}>
                Keine weiteren Adressen vorhanden.
              </p>
            )}
          </section>
        </div>
      )}

      {showForm ? (
        <div className="mt-3 rounded-[10px] border border-black/10 p-3 dark:border-white/[0.07]">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:text-[#6b7a9a]">
            {editingId ? "Adresse bearbeiten" : "Adresse hinzufügen"}
          </div>
          {formError ? (
            <p className="mb-2 text-[12px] text-[#f87171]">{formError}</p>
          ) : null}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: "10px",
            }}
          >
            <div>
              <label htmlFor="addr-type" className={labelClass}>
                Typ
              </label>
              <select
                id="addr-type"
                className={inputClass}
                value={form.address_type}
                onChange={setField("address_type")}
                disabled={saving || editingResidential}
              >
                {typeOptions.map((t) => (
                  <option key={t} value={t}>
                    {ADDRESS_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="addr-label" className={labelClass}>
                Bezeichnung
              </label>
              <input
                id="addr-label"
                type="text"
                className={inputClass}
                value={form.label}
                onChange={setField("label")}
                disabled={saving}
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label htmlFor="addr-street" className={labelClass}>
                Strasse
              </label>
              <input
                id="addr-street"
                type="text"
                className={inputClass}
                value={form.street}
                onChange={setField("street")}
                disabled={saving}
              />
            </div>
            <div>
              <label htmlFor="addr-postal-code" className={labelClass}>
                PLZ
              </label>
              <input
                id="addr-postal-code"
                type="text"
                className={inputClass}
                value={form.postal_code}
                onChange={setField("postal_code")}
                disabled={saving}
              />
            </div>
            <div>
              <label htmlFor="addr-city" className={labelClass}>
                Ort
              </label>
              <input
                id="addr-city"
                type="text"
                className={inputClass}
                value={form.city}
                onChange={setField("city")}
                disabled={saving}
              />
            </div>
            <div>
              <label htmlFor="addr-canton" className={labelClass}>
                Kanton
              </label>
              <input
                id="addr-canton"
                type="text"
                className={inputClass}
                value={form.canton}
                onChange={setField("canton")}
                disabled={saving}
              />
            </div>
            <div>
              <label htmlFor="addr-country" className={labelClass}>
                Land
              </label>
              <input
                id="addr-country"
                type="text"
                className={inputClass}
                value={form.country}
                onChange={setField("country")}
                disabled={saving}
              />
            </div>
            <div>
              <label htmlFor="addr-valid-from" className={labelClass}>
                Gültig ab
              </label>
              <input
                id="addr-valid-from"
                type="date"
                className={inputClass}
                value={form.valid_from}
                onChange={setField("valid_from")}
                disabled={saving}
              />
            </div>
            <div>
              <label htmlFor="addr-valid-to" className={labelClass}>
                Gültig bis
              </label>
              <input
                id="addr-valid-to"
                type="date"
                className={inputClass}
                value={form.valid_to}
                onChange={setField("valid_to")}
                disabled={saving}
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label htmlFor="addr-notes" className={labelClass}>
                Notizen
              </label>
              <input
                id="addr-notes"
                type="text"
                className={inputClass}
                value={form.notes}
                onChange={setField("notes")}
                disabled={saving}
              />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={submitForm}
              disabled={saving}
              className="rounded-[8px] bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] px-3.5 py-2 text-[13px] font-semibold text-white"
              style={{ border: "none", cursor: saving ? "default" : "pointer" }}
            >
              {saving ? "Speichern …" : "Speichern"}
            </button>
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="rounded-[8px] border border-black/10 bg-transparent px-3.5 py-2 text-[13px] font-semibold text-[#64748b] dark:border-white/[0.1] dark:text-[#8090b0]"
              style={{ cursor: saving ? "default" : "pointer" }}
            >
              Abbrechen
            </button>
          </div>
        </div>
      ) : null}

      <AddressChangeModal
        open={changeOpen}
        currentValidFrom={current ? current.valid_from : null}
        saving={changeSaving}
        onClose={() => setChangeOpen(false)}
        onSubmit={handleAddressChangeSubmit}
      />
    </div>
  );
}
